import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar as CalendarIcon, MapPin, Clock, Users, CreditCard, Banknote, UserPlus, Download, Loader2, ArrowLeft, ChevronRight, User, Mail, Phone, Share2, Copy, Check, Link2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatTime = (timeStr) => timeStr ? timeStr.substring(0, 5) + ' Uhr' : '';

export default function PublicEventDetailPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/events/public/${eventId}`)
      .then(r => {
        if (!r.ok) throw new Error('Event nicht gefunden');
        return r.json();
      })
      .then(d => setEvent(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff' }}>
      <Loader2 className="animate-spin" style={{ color: '#d5001c', width: 40, height: 40 }} />
    </div>
  );

  if (error || !event) return (
    <PageShell>
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <CalendarIcon style={{ width: 48, height: 48, margin: '0 auto 16px', color: '#999' }} />
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Event nicht gefunden</h2>
        <p style={{ color: '#666', marginBottom: 24 }}>Dieser Event existiert nicht oder ist nicht mehr verfügbar.</p>
        <Link to="/public/events" style={{ color: '#d5001c', fontWeight: 600, textDecoration: 'none' }}>
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Zurück zur Übersicht
        </Link>
      </div>
    </PageShell>
  );

  const d = new Date(event.date);
  const spots = event.max_participants ? event.max_participants - (event.stats?.total_participants || 0) : null;
  const full = spots !== null && spots <= 0;

  return (
    <PageShell>
      <style>{`
        .ped-html h1 { font-size: 24px; font-weight: 700; color: #111; margin: 0 0 16px; line-height: 1.3; }
        .ped-html h2 { font-size: 20px; font-weight: 700; color: #111; margin: 24px 0 12px; line-height: 1.3; }
        .ped-html h3 { font-size: 17px; font-weight: 700; color: #222; margin: 20px 0 10px; }
        .ped-html h4 { font-size: 15px; font-weight: 700; color: #333; margin: 16px 0 8px; }
        .ped-html p { margin: 0 0 12px; }
        .ped-html ul, .ped-html ol { margin: 12px 0; padding-left: 24px; }
        .ped-html ul { list-style-type: disc; }
        .ped-html ol { list-style-type: decimal; }
        .ped-html li { margin: 6px 0; padding-left: 4px; }
        .ped-html li p { margin: 0; }
        .ped-html strong, .ped-html b { font-weight: 700; }
        .ped-html em, .ped-html i { font-style: italic; }
        .ped-html a { color: #d5001c; text-decoration: underline; }
        .ped-html blockquote { border-left: 3px solid #d5001c; padding-left: 16px; margin: 16px 0; font-style: italic; color: #555; }
        @media (max-width: 600px) {
          .ped-hero { height: 200px !important; }
          .ped-content { padding: 20px 16px !important; }
          .ped-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Hero Image */}
      {event.image_url && (
        <div className="ped-hero" style={{ width: '100%', height: 300, overflow: 'hidden', background: '#1a1a1a' }}>
          <img src={event.image_url} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} />
        </div>
      )}

      {/* Event Content */}
      <div className="ped-content" style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        {/* Back Link */}
        <Link to="/public/events" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#888', fontSize: 13, textDecoration: 'none', marginBottom: 20 }}
          data-testid="back-to-events-link">
          <ArrowLeft size={14} /> Alle Events
        </Link>

        {/* Date Badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#d5001c', color: '#fff', textAlign: 'center', padding: '10px 14px', minWidth: 56, flexShrink: 0 }}>
            <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{d.getDate()}</div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1, marginTop: 2 }}>
              {d.toLocaleDateString('de-CH', { month: 'short' })}
            </div>
            <div style={{ fontSize: 11, fontWeight: 500, marginTop: 1 }}>{d.getFullYear()}</div>
          </div>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 8px', lineHeight: 1.2, color: '#111', fontFamily: "'Oswald', 'Helvetica Neue', sans-serif", textTransform: 'uppercase', letterSpacing: 0.5 }}
              data-testid="event-title">
              {event.title}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 14, color: '#555' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><CalendarIcon size={14} />{formatDate(event.date)}</span>
              {event.time && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Clock size={14} />{formatTime(event.time)}</span>}
              {event.location && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><MapPin size={14} />{event.location}</span>}
            </div>
          </div>
        </div>

        {/* Availability & Price */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #eee' }}>
          {event.price > 0 && (
            <div style={{ background: '#f8f8f8', padding: '8px 14px', fontSize: 14, fontWeight: 600 }}>
              Teilnahmegebühr: <span style={{ color: '#d5001c' }}>CHF {event.price.toFixed(2)}</span>
            </div>
          )}
          {spots !== null && (
            <div style={{ background: full ? '#fef2f2' : '#f0fdf4', padding: '8px 14px', fontSize: 14, fontWeight: 600, color: full ? '#dc2626' : '#16a34a', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Users size={14} />
              {full ? 'Ausgebucht' : `${spots} Plätze frei`}
            </div>
          )}
        </div>

        {/* Share Buttons */}
        <ShareEventButtons event={event} />

        {/* Description */}
        {event.body_html ? (
          <div className="ped-html" style={{ color: '#333', fontSize: 15, lineHeight: 1.8, marginBottom: 24 }}
            dangerouslySetInnerHTML={{ __html: event.body_html }}
            data-testid="event-description" />
        ) : event.lead && (
          <p style={{ color: '#333', fontSize: 15, lineHeight: 1.8, marginBottom: 24 }} data-testid="event-description">{event.lead}</p>
        )}

        {/* Attachments */}
        {event.attachments?.length > 0 && (
          <div style={{ marginBottom: 24, padding: 16, background: '#f8f8f8', border: '1px solid #eee' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 10px', color: '#555' }}>Dokumente</h3>
            {event.attachments.map((att, i) => (
              <a key={i} href={att.url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', color: '#d5001c', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>
                <Download size={14} /> {att.filename}
              </a>
            ))}
          </div>
        )}

        {/* Organizer */}
        {event.organizer && (
          <div style={{ marginBottom: 24, padding: 16, background: '#f8f8f8', border: '1px solid #eee' }} data-testid="public-event-organizer">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <User size={16} color="#d5001c" />
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#555' }}>Organisator (Kontaktperson)</span>
            </div>
            <div style={{ marginLeft: 24 }}>
              <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 4px' }}>{event.organizer.name}</p>
              {event.organizer.email && (
                <a href={`mailto:${event.organizer.email}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#555', textDecoration: 'none', marginBottom: 2 }}>
                  <Mail size={14} color="#d5001c" /> {event.organizer.email}
                </a>
              )}
              {event.organizer.mobile && (
                <a href={`tel:${event.organizer.mobile}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#555', textDecoration: 'none' }}>
                  <Phone size={14} color="#d5001c" /> {event.organizer.mobile}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Member login hint - above guest form */}
        {event.registration_open && (
          <div style={{ textAlign: 'center', padding: '20px 0', marginBottom: 24 }}>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>Bereits Mitglied?</p>
            <a href={`/login?returnUrl=${encodeURIComponent('/events/' + event.id)}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1a1a1a', color: '#fff', padding: '12px 28px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', textDecoration: 'none', letterSpacing: 1 }}
              data-testid="member-login-link">
              Mitglieder-Anmeldung <ChevronRight size={16} />
            </a>
          </div>
        )}

        {/* Guest Registration */}
        {event.registration_open && event.guest_registration_enabled && !full && (
          <GuestRegistrationForm event={event} />
        )}
      </div>

      {/* SHRT Promotion Footer */}
      <SHRTPromoFooter />
    </PageShell>
  );
}

/* ── Page Shell ─────────────────────────── */
function PageShell({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      {/* Header */}
      <header style={{ background: '#1a1a1a', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link to="/public/events" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <img src="https://bilder.shrt.ch/shrt-logo.png" alt="S.H.R.T." style={{ height: 36 }}
            onError={(e) => { e.target.style.display = 'none'; }} />
          <span style={{ color: '#fff', fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>
            S.H.R.T.
          </span>
        </Link>
      </header>

      {/* Content */}
      <div style={{ flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

/* ── Guest Registration Form ─────────────── */
function GuestRegistrationForm({ event }) {
  const [guestData, setGuestData] = useState({ name: '', email: '', phone: '', guests: 0, notes: '', payment_method: 'online' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const price = event.price || 0;
  const totalPersons = 1 + (guestData.guests || 0);
  const totalAmount = price * totalPersons;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!guestData.name.trim() || !guestData.email.trim()) {
      alert('Name und E-Mail sind erforderlich');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/events/public/${event.id}/guest-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(guestData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Fehler bei der Anmeldung');

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setSubmitted(true);
    } catch (error) {
      alert(error.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: '#f0fdf4', border: '2px solid #16a34a', padding: 24, marginBottom: 24, textAlign: 'center' }}
        data-testid="guest-registration-success">
        <div style={{ fontSize: 36, marginBottom: 8 }}>&#10003;</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: '#16a34a' }}>Anmeldung erfolgreich!</h3>
        <p style={{ color: '#555', fontSize: 14 }}>Du erhältst in Kürze eine Bestätigung per E-Mail.</p>
      </div>
    );
  }

  return (
    <div style={{ border: '2px solid #d5001c', marginBottom: 24 }} data-testid="guest-registration-form">
      <div style={{ background: '#d5001c', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserPlus size={18} color="#fff" />
        <h3 style={{ color: '#fff', fontSize: 15, fontWeight: 700, margin: 0, textTransform: 'uppercase', letterSpacing: 1 }}>
          Als Gast anmelden
        </h3>
      </div>
      <form onSubmit={handleSubmit} style={{ padding: 20 }}>
        <div className="ped-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, color: '#888' }}>Name *</label>
            <input type="text" required value={guestData.name} onChange={e => setGuestData({...guestData, name: e.target.value})}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }}
              placeholder="Vor- und Nachname" data-testid="guest-name-input" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, color: '#888' }}>E-Mail *</label>
            <input type="email" required value={guestData.email} onChange={e => setGuestData({...guestData, email: e.target.value})}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }}
              placeholder="ihre@email.ch" data-testid="guest-email-input" />
          </div>
        </div>
        <div className="ped-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, color: '#888' }}>Telefon</label>
            <input type="tel" value={guestData.phone} onChange={e => setGuestData({...guestData, phone: e.target.value})}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }}
              placeholder="+41 79 123 45 67" data-testid="guest-phone-input" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, color: '#888' }}>Begleitpersonen</label>
            <select value={guestData.guests} onChange={e => setGuestData({...guestData, guests: parseInt(e.target.value) || 0})}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
              data-testid="guest-count-input">
              <option value={0}>Keine</option>
              <option value={1}>1 Begleitperson</option>
              <option value={2}>2 Begleitpersonen</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, color: '#888' }}>Bemerkungen</label>
          <textarea value={guestData.notes} onChange={e => setGuestData({...guestData, notes: e.target.value})} rows={2}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
            placeholder="Allergien, spezielle Wünsche..." data-testid="guest-notes-input" />
        </div>

        {/* Payment Method */}
        {event.payment_required && price > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, color: '#888' }}>Zahlungsart *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setGuestData({...guestData, payment_method: 'online'})}
                style={{ flex: 1, padding: '10px 12px', border: guestData.payment_method === 'online' ? '2px solid #d5001c' : '1px solid #ddd',
                  background: guestData.payment_method === 'online' ? '#fff5f5' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}
                data-testid="payment-online-btn">
                <CreditCard size={16} color={guestData.payment_method === 'online' ? '#d5001c' : '#999'} /> Online (Karte/TWINT)
              </button>
              <button type="button" onClick={() => setGuestData({...guestData, payment_method: 'cash'})}
                style={{ flex: 1, padding: '10px 12px', border: guestData.payment_method === 'cash' ? '2px solid #d5001c' : '1px solid #ddd',
                  background: guestData.payment_method === 'cash' ? '#fff5f5' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}
                data-testid="payment-cash-btn">
                <Banknote size={16} color={guestData.payment_method === 'cash' ? '#d5001c' : '#999'} /> Bar vor Ort
              </button>
            </div>
          </div>
        )}

        {/* Price + Submit */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid #eee', paddingTop: 14 }}>
          {event.payment_required && price > 0 && (
            <div style={{ fontSize: 15, color: '#333' }}>
              <strong>CHF {totalAmount.toFixed(2)}</strong>
              <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>({totalPersons} Pers.)</span>
            </div>
          )}
          <button type="submit" disabled={submitting}
            style={{ marginLeft: 'auto', padding: '12px 28px', border: 'none', background: '#d5001c', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', textTransform: 'uppercase', letterSpacing: 1, opacity: submitting ? 0.7 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 6 }}
            data-testid="guest-submit-btn">
            {submitting ? 'Wird verarbeitet...' : guestData.payment_method === 'online' && event.payment_required && price > 0 ? 'Zur Zahlung' : 'Anmelden'}
            {!submitting && <ChevronRight size={16} />}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── Share Event Buttons ──────────────────── */
function ShareEventButtons({ event }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = window.location.href;
  const d = new Date(event.date);
  const dateStr = d.toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const shareText = `${event.title} – ${dateStr}${event.time ? `, ${event.time} Uhr` : ''}${event.location ? `, ${event.location}` : ''}`;

  const handleWhatsApp = () => {
    const msg = `${shareText}\n\nInfos & Anmeldung:\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(shareText);
    const body = encodeURIComponent(`${shareText}\n\nInfos & Anmeldung: ${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: event.title, text: shareText, url: shareUrl });
    } catch { /* user cancelled */ }
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }} data-testid="share-event-buttons">
      {/* WhatsApp - Primary */}
      <button onClick={handleWhatsApp}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', border: 'none', background: '#25D366', color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5 }}
        data-testid="share-whatsapp-btn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        WhatsApp
      </button>

      {/* Copy Link */}
      <button onClick={handleCopy}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #ddd', background: copied ? '#f0fdf4' : '#fff', color: copied ? '#16a34a' : '#333',
          fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        data-testid="share-copy-link-btn">
        {copied ? <Check size={16} /> : <Copy size={16} />}
        {copied ? 'Kopiert!' : 'Link kopieren'}
      </button>

      {/* Email */}
      <button onClick={handleEmail}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #ddd', background: '#fff', color: '#333',
          fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        data-testid="share-email-btn">
        <Mail size={16} />
        E-Mail
      </button>

      {/* Native Share (mobile) */}
      {canNativeShare && (
        <button onClick={handleNativeShare}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #ddd', background: '#fff', color: '#333',
            fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          data-testid="share-native-btn">
          <Share2 size={16} />
          Teilen
        </button>
      )}
    </div>
  );
}

/* ── SHRT Promotion Footer ───────────────── */
function SHRTPromoFooter() {
  return (
    <div style={{ background: '#1a1a1a', color: '#fff', marginTop: 'auto' }}>
      {/* Red separator line */}
      <div style={{ height: 4, background: '#d5001c' }} />

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
        <img src="https://bilder.shrt.ch/shrt-logo.png" alt="S.H.R.T." style={{ height: 60, marginBottom: 16 }}
          onError={(e) => { e.target.style.display = 'none'; }} />

        <h2 style={{ fontFamily: "'Oswald', 'Helvetica Neue', sans-serif", fontSize: 24, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, margin: '0 0 12px' }}
          data-testid="promo-heading">
          Swiss Historic Racing Team
        </h2>

        <p style={{ fontSize: 15, lineHeight: 1.7, color: '#ccc', maxWidth: 520, margin: '0 auto 8px' }}>
          Wir sind ein Verein für Liebhaber historischer Rennfahrzeuge und organisieren regelmässig
          Ausfahrten, Trackdays und gesellige Anlässe.
        </p>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: '#ccc', maxWidth: 520, margin: '0 auto 28px' }}>
          Du teilst unsere Leidenschaft? Werde Teil unserer Gemeinschaft!
        </p>

        <a href="/mitglied-werden"
          style={{
            display: 'inline-block',
            background: '#d5001c',
            color: '#fff',
            padding: '14px 36px',
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'uppercase',
            textDecoration: 'none',
            letterSpacing: 1.5,
            transition: 'background 0.2s'
          }}
          data-testid="become-member-cta">
          Mitglied werden <ChevronRight size={16} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
        </a>

        {/* Footer Links */}
        <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #333' }}>
          <div style={{ textAlign: 'center', lineHeight: 1.8, fontSize: 13, color: '#aaa', marginBottom: 16 }}>
            <p style={{ fontWeight: 700, color: '#fff' }}>S.H.R.T.</p>
            <p>Swiss Historic Racing Team</p>
            <p style={{ fontSize: 11 }}>Verein gem&auml;ss Art. 60 ff. ZGB</p>
            <p style={{ marginTop: 8 }}>Kuno Sch&auml;r &middot; St. Niklausstrasse 1 &middot; CH-4502 Solothurn</p>
            <p>Tel. <a href="tel:+41794153031" style={{ color: '#aaa', textDecoration: 'none' }}>+41 79 415 30 31</a> &middot; <a href="mailto:info@kunoschaer.ch" style={{ color: '#aaa', textDecoration: 'none' }}>info@kunoschaer.ch</a></p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
            <a href="/datenschutz" style={{ color: '#888', fontSize: 12, textDecoration: 'none' }}>Datenschutz</a>
            <a href="/agb" style={{ color: '#888', fontSize: 12, textDecoration: 'none' }}>AGB</a>
            <a href="/impressum" style={{ color: '#888', fontSize: 12, textDecoration: 'none' }}>Impressum</a>
            <a href="/public/events" style={{ color: '#888', fontSize: 12, textDecoration: 'none' }}>Events</a>
          </div>
        </div>
        <p style={{ color: '#555', fontSize: 11, marginTop: 12 }}>
          &copy; {new Date().getFullYear()} Swiss Historic Racing Team
        </p>
      </div>
    </div>
  );
}
